import { Motion, Presence } from "@motionone/solid";
import { Show, createEffect, createMemo, createSignal, onCleanup, onMount } from "solid-js";
import { Portal } from "solid-js/web";
import { CloseButton } from "../components/CloseButton";
import { useHeroForm } from "../contexts/HeroFormContext";
import { MobileCustomizationFields } from "./ProductHeroFormFields";
import { AddToBagButton, NeumorphicAddToBagButton } from "./AddToBagButton";
import { AccentContainerV2, AccentsContainer } from "./AccentsContainer";
import { AdditionalPriceIndicator } from "../components/AdditionalPriceIndicator";
import { getCustomChargeItems, getPriceFormatted } from "../js/itemPropsHelper";
import { RectangleButton } from "./Buttons";

export default function StyleCustomizationModal(props) {
  const {
    styleCustomizations: {
      styleCustomizations,
      handlers: {
        clearStyleCustomizations
      }
    },
    color: {
      color
    },
    getters: {
      getExperimentFlags
    },
    accentCustomizations: {
      accentFormData,
      accentSettings,
      handlers: {
        clearAccentCustomization
      }
    },
    heroSectionSettings
  } = useHeroForm();

  const [tabSelected, setTabSelected] = createSignal(Object.keys(styleCustomizations())?.filter(key => Object.keys(styleCustomizations()[key].options).length > 1)?.length > 0 ? 0 : 1);

  const style = {
    "z-index": "100000",
    "background": "url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAAQCAYAAAAf8/9hAAABdWlDQ1BrQ0dDb2xvclNwYWNlRGlzcGxheVAzAAAokXWQvUvDUBTFT6tS0DqIDh0cMolD1NIKdnFoKxRFMFQFq1OafgltfCQpUnETVyn4H1jBWXCwiFRwcXAQRAcR3Zw6KbhoeN6XVNoi3sfl/Ticc7lcwBtQGSv2AijplpFMxKS11Lrke4OHnlOqZrKooiwK/v276/PR9d5PiFlNu3YQ2U9cl84ul3aeAlN//V3Vn8maGv3f1EGNGRbgkYmVbYsJ3iUeMWgp4qrgvMvHgtMunzuelWSc+JZY0gpqhrhJLKc79HwHl4plrbWD2N6f1VeXxRzqUcxhEyYYilBRgQQF4X/8044/ji1yV2BQLo8CLMpESRETssTz0KFhEjJxCEHqkLhz634PrfvJbW3vFZhtcM4v2tpCAzidoZPV29p4BBgaAG7qTDVUR+qh9uZywPsJMJgChu8os2HmwiF3e38M6Hvh/GMM8B0CdpXzryPO7RqFn4Er/QcXKWq8UwZBywAAAFxlWElmTU0AKgAAAAgABAEGAAMAAAABAAIAAAESAAMAAAABAAEAAAEoAAMAAAABAAIAAIdpAAQAAAABAAAAPgAAAAAAAqACAAQAAAABAAAAEKADAAQAAAABAAAAEAAAAADmO95bAAACC2lUWHRYTUw6Y29tLmFkb2JlLnhtcAAAAAAAPHg6eG1wbWV0YSB4bWxuczp4PSJhZG9iZTpuczptZXRhLyIgeDp4bXB0az0iWE1QIENvcmUgNi4wLjAiPgogICA8cmRmOlJERiB4bWxuczpyZGY9Imh0dHA6Ly93d3cudzMub3JnLzE5OTkvMDIvMjItcmRmLXN5bnRheC1ucyMiPgogICAgICA8cmRmOkRlc2NyaXB0aW9uIHJkZjphYm91dD0iIgogICAgICAgICAgICB4bWxuczp0aWZmPSJodHRwOi8vbnMuYWRvYmUuY29tL3RpZmYvMS4wLyI+CiAgICAgICAgIDx0aWZmOlJlc29sdXRpb25Vbml0PjI8L3RpZmY6UmVzb2x1dGlvblVuaXQ+CiAgICAgICAgIDx0aWZmOk9yaWVudGF0aW9uPjE8L3RpZmY6T3JpZW50YXRpb24+CiAgICAgICAgIDx0aWZmOkNvbXByZXNzaW9uPjE8L3RpZmY6Q29tcHJlc3Npb24+CiAgICAgICAgIDx0aWZmOlBob3RvbWV0cmljSW50ZXJwcmV0YXRpb24+MjwvdGlmZjpQaG90b21ldHJpY0ludGVycHJldGF0aW9uPgogICAgICA8L3JkZjpEZXNjcmlwdGlvbj4KICAgPC9yZGY6UkRGPgo8L3g6eG1wbWV0YT4KlqhK0AAAAK5JREFUOBGNk0sOgzAMRK2oSCxaiSP0/pfiGuxaT+iEfOohSFGI8Hu2E2Lp+fiU8Vp229a3RY9/Sx5T4p21sriDKe0kp2AW/iMxlBSVvXo2DHLN/KvEFJz79QRK0ki5ANBslpIQ4jzAPKUZSQgLSWJmzEe9CN7vY/r+q+zRho+5eok4alNHxGOMMmcWQUqiYLDlVw4lY4O5av4nzWWakSCGMC7iJcBuq3a8kh6G4As0j1Qn0t5eCwAAAABJRU5ErkJggg==), rgba(0,0,0,0.3)",
    "background-size": "6px",
    "backdrop-filter": "grayscale(0%), brightness(.5)"
  }

  onMount(() => {
    const close = (e) => {
      if (e.key === "Escape") {
        handleModalStateChange(false);
      }
    }
    document.addEventListener('keydown', close);
    onCleanup(() => {
      document.removeEventListener('keydown', close);
    })
  })

  const [modalState, setModalState] = createSignal(false)
  const handleModalStateChange = (state) => {
    document.body.style.overflow = state ? 'hidden' : '';

    setModalState(state);
  }

  const handleEmptySpaceClick = (e) => {
    if (e.target.classList.contains("empty-parent")) {
      handleModalStateChange(false);
    }
  }

  const accentChargeVariant = createMemo(() => {
    return accentSettings.options?.find(option => option.id == accentFormData.selected)?.chargeVariant;
  })

  const accentCharge = createMemo(() => {
    const chargeVariant = accentChargeVariant();

    if (!chargeVariant) {
      return;
    }

    const chargeVariants = Object.entries(accentSettings.options?.find(option => option.id == accentFormData.selected)?.fields)
      .map(([fieldId, fieldInfo]) => {
        const fieldSelected = accentFormData.option_inputs?.[accentFormData.selected]?.[fieldId];
        const option = fieldInfo?.options?.find(option => option.id == fieldSelected);

        return option?.chargeVariant;
      })?.filter(variant => variant);
    const price = chargeVariant?.price + (chargeVariants?.map(variant => variant?.price).reduce((prev, curr) => prev + (curr ?? 0), 0));

    return `+${getPriceFormatted(price / 100)}`
  })

  const styleCharge = createMemo(() => {
    const customizationItems = getCustomChargeItems(styleCustomizations());

    return customizationItems?.length > 0
      ? getPriceFormatted(customizationItems.map(item => item.price / 100).reduce((prev, curr) => prev + curr, 0))
      : null;
  })

  window.openCustomizationWindow = (tabIndex) => {
    handleModalStateChange(true);
    setTabSelected(tabIndex);
  }

  return (
    <Show when={Object.keys(styleCustomizations())?.filter(key => Object.keys(styleCustomizations()[key].options).length > 1)?.length > 0}>
      <Show
        when={heroSectionSettings.isNewVersion}
        fallback={(
          <button
            type="button"
            onClick={() => {
              handleModalStateChange(true)
              if (typeof (props.clickHandler) != 'undefined') {
                props.clickHandler();
              }
            }}
            class="py-2 px-4 text-base border flex justify-center items-center rounded-lg max-w-sm border-ink text-ink bg-canvas-light hover:bg-canvas-lighter"
          >
            Customize
          </button>
        )}
      >
        <RectangleButton
          onClick={() => {
            handleModalStateChange(true)
            if (typeof (props.clickHandler) != 'undefined') {
              props.clickHandler();
            }
          }}
          class={"py-2 px-4 text-base"}
          label="Customize"
        />
      </Show>

      <Portal>
        <Presence>
          <Show when={modalState()}>
            <Motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.2 }}
              style={style}
              class="h-full w-full fixed top-0 bottom-0 left-0 right-0 empty-parent"
              onClick={handleEmptySpaceClick}
            >
              <Motion.div
                initial={{ x: 100 }}
                animate={{ x: 0 }}
                exit={{ x: 200 }}
                transition={{ duration: 0.5 }}
                class={`mix-blend-normal shadow-lg bottom-0 absolute right-0  w-screen h-screen-actual max-w-[90vw] sm:max-w-sm bg-canvas border-frame  flex flex-col `}
              >
                <div class=" w-full ">
                  <div class="flex flex-col">
                    <div class="flex border-frame border-b-outer divide-x-outer divide-frame w-full justify-between items-stretch">
                      <h3 class="font-bold grow  p-2 text-lg">Customizations</h3>
                      <CloseButton
                        className="p-2"
                        onClickHandler={() => handleModalStateChange(false)}
                      />
                    </div>
                    <StyleListSelector
                      tabSelected={tabSelected()}
                      setTabSelected={setTabSelected}
                    />
                    <div class="absolute bottom-0 left-0 right-0">
                      <Show
                        when={styleCharge()}
                      >
                        <AdditionalPriceIndicator
                          class="mx-1 mt-1"
                          label="Style Customization"
                          price={`+${styleCharge()}`}
                          onClear={() => clearStyleCustomizations(color()?.options?.[color()?.selected]?.defaultStyles ?? {})}
                        />
                      </Show>
                      <Show
                        when={accentCharge() != undefined}
                      >
                        <AdditionalPriceIndicator
                          class="mx-1 mt-1"
                          label="Monogram"
                          price={accentCharge()}
                          onClear={clearAccentCustomization}
                        />
                      </Show>
                      <div class="p-1">
                        <NeumorphicAddToBagButton
                          id="customize-save"
                          buttonText={'Save'}
                          handleAddToBagClick={async () => {
                            handleModalStateChange(false)
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </Motion.div>
            </Motion.div>
          </Show>
        </Presence>
      </Portal>
    </Show>
  );

}



function StyleListSelector(props) {
  const {
    sizeSettings: {
      sizeData,
      diyData,
      measurementsInfo,
      measurementOptions,
      handlers: { handleMinMaxChangeOnFit },
    },
    styleCustomizations: {
      styleCustomizations,
      handlers: { handleStyleSelection },
    },
    fit: {
      fit,
      handlers: { handleFitSelection },
    },
    accentCustomizations,
    handlers: { handleCustomizationChange },
    getters: {
      getExperimentFlags
    }
  } = useHeroForm();

  const accentSettings = accentCustomizations.accentSettings;

  const handleStyleSelectionProxy = (selectedOption) => {
    console.log("inside modal : ", selectedOption);

    const id = selectedOption.id || handleize(selectedOption.label);
    handleStyleSelection(id, selectedOption);
    handleCustomizationChange(diyData(), measurementsInfo, {
      sizeCustomization: sizeData()["selected"],
      fitCustomization: fit()["selected"],
      styleCustomizations: styleCustomizations(),
      measurementOption: measurementOptions()["selected"],
    });
  };

  const customChargeItems = createMemo(() => getCustomChargeItems(styleCustomizations()));
  const styleCharge = createMemo(() => {
    const customizationItems = getCustomChargeItems(styleCustomizations());

    return customizationItems?.length > 0
      ? getPriceFormatted(customizationItems.map(item => item.price / 100).reduce((prev, curr) => prev + curr, 0))?.replace(' ', '')
      : null;
  })

  const accentChargeVariant = createMemo(() => {
    return accentSettings.options?.find(option => option.id == accentCustomizations.accentFormData.selected)?.chargeVariant;
  })

  const heightCoeffMultiplier = () => {
    const variant = accentChargeVariant();
    const charge = styleCharge();

    // if(charge) return 1;
    // return 0;

    if (!variant && !charge) return 0;
    if (variant && charge) return 2;

    return 1;
  }

  const customizationsArr = () => {
    return Object.values(styleCustomizations())
      .filter(
        (customization) =>
          Object.values(customization.options).some((option) => option.isEnabled)
      )
      .map((customization) => {
        return {
          customization,
          selectionHandler: handleStyleSelectionProxy,
          type: 'STYLE'
        };
      });
  };

  return (
    <div
      class="themed-scrollbar overflow-y-auto"
      style={{
        height: `calc(100vh - 140px - ${38 * (heightCoeffMultiplier())}px)`,
      }}
    >
      {/* <Show when={Object.keys(styleCustomizations())?.filter(key => Object.keys(styleCustomizations()[key].options).length > 1)?.length > 0 && accentSettings.options?.length > 0}>
        <div class="flex items-center justify-center w-full pt-1 border-b-frame border-b sticky top-0 bg-canvas z-10">
          <div onClick={() => props.setTabSelected(0)} class="w-full cursor-pointer text-center border-b-2" classList={{
            "text-ink font-semibold border-b-ink": props.tabSelected == 0,
            "border-b-transparent": props.tabSelected != 0
          }}>Style</div>
          <div onClick={() => props.setTabSelected(1)} class="w-full cursor-pointer text-center border-b-2" classList={{
            "text-ink font-semibold border-b-ink": props.tabSelected == 1,
            "border-b-transparent": props.tabSelected != 1
          }}>Monogram</div>
        </div>
      </Show> */}
      <Show when={props.tabSelected == 0}>
        <div>
          <Show when={customChargeItems()?.length == 1}>
            <div class="px-2 py-1 text-xs border-b border-frame">
              Get any number of style customisations at a flat fee of {styleCharge()}
            </div>
          </Show>
          <AccentContainerV2
            {...accentCustomizations}
            isCustomizationList={true}
            class={``}
          />
          <For each={Object.values(customizationsArr())}>
            {(props, index) => {
              return (
                <MobileCustomizationFields
                  customization={props.customization} showOnDesktop={true}
                  class={``}
                />
              );
            }}
          </For>
        </div>
      </Show>
      {/* <Show when={props.tabSelected == 1}>
        <AccentList
          {...accentCustomizations}
          class={``}
        />
      </Show> */}
    </ div>
  )
}